import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, Money, PageHead, RiskBar, Severity, Skel } from '../components/ui'
import { thaiDate } from '../lib/format'

type VendorStatus = 'active' | 'watchlist' | 'blocked'
type RiskBand = 'low' | 'medium' | 'high' | 'critical'

interface VendorRow {
  id: string
  code: string
  name: string
  tax_id: string
  bank_account: string
  country: string
  status: VendorStatus
  risk_score: number
  risk_band: RiskBand
  last_review_at?: string
  notes: string
  paid_satang: number
  txn_count: number
  created_at: string
  updated_at: string
}

interface RiskComponent {
  key: string
  label: string
  points: number
  max: number
  detail: string
}

interface RiskInputs {
  code: string
  country: string
  missing_tax_id: boolean
  distinct_accounts: number
  concentration_share: number
  days_since_review: number
  paid_satang: number
  txn_count: number
  tenant_paid_satang: number
}

interface RiskAssessment {
  score: number
  band: RiskBand
  components: RiskComponent[]
  inputs: RiskInputs
}

interface RiskRefresh {
  vendor: VendorRow
  assessment: RiskAssessment
}

interface Draft {
  id: string
  code: string
  name: string
  tax_id: string
  bank_account: string
  country: string
  status: VendorStatus
  notes: string
}

const EMPTY_DRAFT: Draft = {
  id: '', code: '', name: '', tax_id: '', bank_account: '', country: 'TH', status: 'active', notes: '',
}

const STATUS_LABEL: Record<VendorStatus, string> = {
  active: 'ใช้งาน',
  watchlist: 'เฝ้าระวัง',
  blocked: 'ระงับ',
}

const STATUS_TONE: Record<VendorStatus, string> = {
  active: 'var(--color-emerald-soft)',
  watchlist: 'var(--color-amber)',
  blocked: 'var(--color-critical)',
}

function maskTax(id: string): string {
  const v = (id ?? '').trim()
  return v === '' ? '—' : `••••${v.slice(-4)}`
}

export function Vendors() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['vendors'], queryFn: () => api<VendorRow[]>('/v1/vendors') })

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [detail, setDetail] = useState<RiskRefresh | null>(null)
  const [err, setErr] = useState('')

  const save = useMutation({
    mutationFn: (d: Draft) => apiWrite<VendorRow>('/v1/vendors', 'POST', {
      id: d.id, code: d.code, name: d.name, tax_id: d.tax_id,
      bank_account: d.bank_account, country: d.country, status: d.status,
      notes: d.notes, last_review_at: '',
    }),
    onSuccess: () => {
      setOpen(false)
      setDraft(EMPTY_DRAFT)
      setErr('')
      void qc.invalidateQueries({ queryKey: ['vendors'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const refresh = useMutation({
    mutationFn: (id: string) => apiWrite<RiskRefresh>(`/v1/vendors/${id}/risk-refresh`, 'POST'),
    onSuccess: (res) => {
      setDetail(res)
      setErr('')
      void qc.invalidateQueries({ queryKey: ['vendors'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  function startCreate() {
    if (open && draft.id === '') {
      setOpen(false)
      return
    }
    setDraft(EMPTY_DRAFT)
    setErr('')
    setOpen(true)
  }

  function startEdit(v: VendorRow) {
    setDraft({
      id: v.id, code: v.code, name: v.name, tax_id: v.tax_id,
      bank_account: v.bank_account, country: v.country, status: v.status, notes: v.notes,
    })
    setErr('')
    setOpen(true)
  }

  const rows = q.data ?? []

  return (
    <>
      <PageHead
        title="ทะเบียนผู้ขาย"
        sub="คู่ค้าที่ต้องสอบทาน พร้อมคะแนนความเสี่ยงที่คำนวณจากหลักฐานในบัญชีแยกประเภท"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={startCreate}>
            {open ? 'ปิดฟอร์ม' : 'เพิ่มผู้ขาย'}
          </button>
        ) : undefined}
      />

      {open && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">{draft.id ? `แก้ไขผู้ขาย · ${draft.code}` : 'เพิ่มผู้ขายใหม่'}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-code">รหัสผู้ขาย</label>
              <input id="v-code" className="input mono" value={draft.code} maxLength={32}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="V-7007" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-name">ชื่อผู้ขาย</label>
              <input id="v-name" className="input" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="บริษัท ตัวอย่าง จำกัด" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-tax">เลขประจำตัวผู้เสียภาษี</label>
              <input id="v-tax" className="input mono" inputMode="numeric" maxLength={13} value={draft.tax_id}
                onChange={(e) => setDraft({ ...draft, tax_id: e.target.value.replace(/\D/g, '') })}
                placeholder="0105558000000" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-bank">เลขบัญชีธนาคาร</label>
              <input id="v-bank" className="input mono" inputMode="numeric" maxLength={15} value={draft.bank_account}
                onChange={(e) => setDraft({ ...draft, bank_account: e.target.value.replace(/\D/g, '') })}
                placeholder="1234567890" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-country">ประเทศ</label>
              <input id="v-country" className="input mono" maxLength={2} value={draft.country}
                onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
                placeholder="TH" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="v-status">สถานะ</label>
              <select id="v-status" className="select" style={{ width: '100%' }} value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as VendorStatus })}>
                <option value="active">ใช้งาน</option>
                <option value="watchlist">เฝ้าระวัง</option>
                <option value="blocked">ระงับ</option>
              </select>
            </div>
            <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
              <label htmlFor="v-notes">หมายเหตุการสอบทาน</label>
              <textarea id="v-notes" className="input" value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="เหตุผลที่ขึ้นบัญชีเฝ้าระวัง หรือข้อควรระวังในการจ่ายเงิน"
                style={{ width: '100%', height: 74, paddingTop: 8, resize: 'vertical' }} />
            </div>
          </div>
          {err && <div className="form-error">{err}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={save.isPending || draft.code === '' || draft.name === ''}
              onClick={() => { setErr(''); save.mutate(draft) }}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              รหัสผู้ขายต้องตรงกับรหัสคู่ค้าในบัญชีแยกประเภท เพื่อให้ยอดจ่ายและคะแนนความเสี่ยงเชื่อมกัน
            </span>
          </div>
        </div>
      )}

      {q.isLoading ? <Skel h={220} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีผู้ขาย" body="เพิ่มผู้ขายรายแรกเพื่อเริ่มการสอบทานคู่ค้า" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>ผู้ขาย</th>
                <th>เลขผู้เสียภาษี</th>
                <th>สถานะ</th>
                <th>ความเสี่ยง</th>
                <th className="num">ยอดจ่ายสะสม</th>
                <th className="num">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id}>
                  <td>
                    <div>{v.name}</div>
                    <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{v.code}</div>
                  </td>
                  <td className="mono dim">{maskTax(v.tax_id)}</td>
                  <td>
                    <span className="chip" style={{ color: STATUS_TONE[v.status], borderColor: 'currentColor' }}>
                      {STATUS_LABEL[v.status]}
                    </span>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 12 }}>
                      <Severity value={v.risk_band} />
                      <RiskBar score={v.risk_score} />
                    </div>
                  </td>
                  <td className="num">
                    <Money satang={v.paid_satang} compact />
                    <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{v.txn_count} รายการ</div>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {canWrite && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(v)}>แก้ไข</button>
                          <button className="btn btn-ghost btn-sm"
                            disabled={refresh.isPending && refresh.variables === v.id}
                            onClick={() => { setErr(''); refresh.mutate(v.id) }}>
                            {refresh.isPending && refresh.variables === v.id ? 'กำลังประเมิน…' : 'ประเมินความเสี่ยงใหม่'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && !open && <div className="form-error">{err}</div>}

      {detail && (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="card-label">ผลการประเมินความเสี่ยง</div>
              <div style={{ marginTop: 8, fontSize: 14 }}>{detail.vendor.name}</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                {detail.vendor.last_review_at
                  ? `ทบทวนล่าสุด ${thaiDate(detail.vendor.last_review_at)}`
                  : 'ยังไม่เคยบันทึกการทบทวน'}
              </div>
            </div>
            <div className="row" style={{ gap: 14 }}>
              <Severity value={detail.assessment.band} />
              <RiskBar score={detail.assessment.score} />
            </div>
          </div>

          <div className="stack" style={{ marginTop: 20, gap: 14 }}>
            {detail.assessment.components.map((c) => (
              <div key={c.key}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                  <span>{c.label}</span>
                  <span className="tnum dim">{c.points}/{c.max}</span>
                </div>
                <div className="bar" style={{ width: '100%', marginTop: 7 }}>
                  <i style={{
                    width: `${c.max > 0 ? (c.points / c.max) * 100 : 0}%`,
                    background: c.points > 0 ? 'var(--color-amber)' : 'var(--color-emerald)',
                  }} />
                </div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{c.detail}</div>
              </div>
            ))}
          </div>

          <div className="dim" style={{ fontSize: 11.5, marginTop: 18 }}>
            หลักฐานประกอบ: {detail.assessment.inputs.distinct_accounts} บัญชีรับเงิน ·
            สัดส่วนยอดจ่าย {(detail.assessment.inputs.concentration_share * 100).toFixed(1)}% ·
            {detail.assessment.inputs.days_since_review < 0
              ? ' ยังไม่เคยทบทวน'
              : ` ทบทวนล่าสุด ${detail.assessment.inputs.days_since_review} วันก่อน`}
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
            เกณฑ์คะแนน: บัญชีรับเงินเปลี่ยน 25 · การกระจุกตัว 15 · ความเก่าของการทบทวน 20 ·
            ประเทศต้องห้าม 25 · ขาดเลขประจำตัวผู้เสียภาษี 15
          </div>
        </div>
      )}
    </>
  )
}
